import type { SummerMonthlyWeather } from '@/cms/fields/types/content-page'
import type { TemperatureTier } from '@/scopes/content/domain/weather'
import { Icon } from '@sunwebgroup/uds-ui/icon'
import { Typography } from '@sunwebgroup/uds-ui/typography'
import { cn } from '@sunwebgroup/uds-ui/utils'
import { format } from 'date-fns'
import { useIntl } from '@/intl'
import { useDateFnsLocale } from '@/scopes/common/hooks/useDateFnsLocale'
import { SummerTemperatureGraphBar } from '@/scopes/content/components/Weather/SummerTemperatureGraphBar'
import {
  WeatherGraphColumn,
  WeatherGraphColumnHeading,
} from '@/scopes/content/components/Weather/WeatherGraph'
import styles from './SummerWeatherGraphColumn.module.css'

interface SummerWeatherGraphColumnProps {
  data: SummerMonthlyWeather
  maxTempRatio: number
  tier: TemperatureTier
  showWaterTemperature: boolean
  showDaysOfRain: boolean
}

export function SummerWeatherGraphColumn({ data, maxTempRatio, tier, showWaterTemperature, showDaysOfRain }: SummerWeatherGraphColumnProps) {
  const intl = useIntl()
  const dateFnsLocale = useDateFnsLocale()
  const monthShortName = format(new Date(2026, data.month - 1, 1), 'MMM', { locale: dateFnsLocale })
    .replace('.', '')

  return (
    <WeatherGraphColumn>
      <WeatherGraphColumnHeading>
        <Typography variant="body-l-regular-tight" className={styles.monthName}>{monthShortName}</Typography>
      </WeatherGraphColumnHeading>
      <SummerTemperatureGraphBar
        maxTemp={data.maxTemp}
        minTemp={data.minTemp}
        maxTempRatio={maxTempRatio}
        tier={tier}
      />
      {showWaterTemperature && (
        <div className={cn(styles.row, styles.waterTemperatureRow)}>
          {data.waterTemp != null && (
            <>
              <Icon glyph="WaterWaves" className={styles.waterIcon} />
              <Typography variant="body-l-bold-tight">
                {data.waterTemp}
                °
              </Typography>
            </>
          )}
        </div>
      )}
      {showDaysOfRain && (
        <div className={cn(styles.row, styles.daysOfRainRow)}>
          {data.daysOfRain != null && (
            <>
              <Icon glyph="CloudAndRain" />
              <Typography variant="body-l-bold-tight">{data.daysOfRain}</Typography>
              <Typography variant="body-s-regular-tight">{intl.formatMessage({ id: 'content_weather_label_days' })}</Typography>
            </>
          )}
        </div>
      )}
    </WeatherGraphColumn>
  )
}
